// §9.3 `menu` + `command` — the tree an app publishes, and the id that comes back.
//
// A menu belongs to a **window instance**, not to an app: two Notepad windows can be editing
// different documents with different things greyed out, and §9.3's `publish` arrives on a
// connection, which is per instance. The macOS global menu bar (§6.3, M11) reads the *focused*
// window's tree from exactly here, which is why the lookup is by instance and the shell decides
// which instance that is.
//
// The tree is stored as published and never merged with anything. A skin renders it; nothing
// rewrites it.
import type { MenuNode } from '@hangar/shared';

export interface MenuService {
  /** §9.3 `menu/publish`. Replaces whatever that window had. */
  publish(instanceKey: string, tree: readonly MenuNode[]): void;
  /** The tree for one window, or an empty list. Never null: a skin should not branch on it. */
  of(instanceKey: string): readonly MenuNode[];
  /** True when this window has published anything — what a skin uses to decide to draw a bar. */
  has(instanceKey: string): boolean;
  /** Forget a window's tree. Called when the window closes. */
  clear(instanceKey: string): void;
  /** Every leaf id in a tree, for validation and for the §11.12 inspector. */
  idsOf(instanceKey: string): string[];
}

const NONE: readonly MenuNode[] = Object.freeze([]);

/** Depth-first leaf ids. A node with `items` is a submenu and fires nothing itself. */
export function menuLeafIds(tree: readonly MenuNode[]): string[] {
  const ids: string[] = [];
  const walk = (nodes: readonly MenuNode[]): void => {
    for (const node of nodes) {
      if (node.items && node.items.length > 0) walk(node.items);
      else ids.push(node.id);
    }
  };
  walk(tree);
  return ids;
}

export function createMenuService(options: { onPublish?: (instanceKey: string) => void } = {}): MenuService {
  const trees = new Map<string, readonly MenuNode[]>();

  return {
    publish(instanceKey, tree) {
      // Copied on the way in: the caller's array came off a `postMessage` or out of an app's own
      // state, and either one could keep mutating it afterwards.
      trees.set(instanceKey, Object.freeze([...tree]));
      options.onPublish?.(instanceKey);
    },
    of: (instanceKey) => trees.get(instanceKey) ?? NONE,
    has: (instanceKey) => trees.has(instanceKey),
    clear(instanceKey) {
      trees.delete(instanceKey);
    },
    idsOf: (instanceKey) => menuLeafIds(trees.get(instanceKey) ?? NONE),
  };
}
